/**
 * v2 Game API service layer.
 *
 * All endpoints receive full character data (no ID lookups on backend).
 */
import { apiPost } from './client';
import type { Character, CharacterInput, CharacterMetrics } from '@shared/types/character';
import type { FamilyMember, FamilyMemberInput } from '@shared/types/family';
import type { Act1Card, Act2Card, TopicStage } from '@shared/types/cards';
import type { ChatMessage, Act1Result, Act2Result, Act3Result, FinalReport } from '@shared/types/gameState';
import type {
  GenerateCharacterResponse,
  GenerateFamilyResponse,
  Act1ChatResponse,
  Act1EvaluateResponse,
  Act2ChatResponse,
  Act2CardsResponse,
  Act2EvaluateResponse,
  Act3WeekResponse,
  Act3EvaluateResponse,
  GenerateReportResponse,
} from '@shared/types/api';

// ============================================
// 角色 & 亲友生成
// ============================================

export async function generateCharacter(input: CharacterInput): Promise<Character> {
  const res = await apiPost<GenerateCharacterResponse>('/api/generate-character', { input });
  return res.character;
}

export async function generateFamily(
  members: FamilyMemberInput[],
  protagonist: Character,
): Promise<FamilyMember[]> {
  const res = await apiPost<GenerateFamilyResponse>('/api/generate-family', { members, protagonist });
  return res.members;
}

// ============================================
// 第一幕
// ============================================

export async function act1Chat(params: {
  protagonistA: Character;
  protagonistB: Character;
  familyA: FamilyMember[];
  familyB: FamilyMember[];
  history: ChatMessage[];
  round: number;
  familyAttitudes: Record<string, number>;
  playedCard?: Act1Card;
  cardTarget?: string;
}): Promise<Act1ChatResponse> {
  return apiPost<Act1ChatResponse>('/api/act1/chat', params);
}

export async function act1Evaluate(params: {
  protagonistA: Character;
  protagonistB: Character;
  familyA: FamilyMember[];
  familyB: FamilyMember[];
  history: ChatMessage[];
  familyAttitudes: Record<string, number>;
}): Promise<Act1EvaluateResponse> {
  return apiPost<Act1EvaluateResponse>('/api/act1/evaluate', params);
}

// ============================================
// 第二幕
// ============================================

export async function act2Chat(params: {
  protagonistA: Character;
  protagonistB: Character;
  history: ChatMessage[];
  round: number;
  metricsA: CharacterMetrics;
  metricsB: CharacterMetrics;
  topicStage: TopicStage;
  scene: string;
  playedCard?: Act2Card;
  cardTarget?: string;
  act1Result?: Act1Result;
}): Promise<Act2ChatResponse> {
  return apiPost<Act2ChatResponse>('/api/act2/chat', params);
}

export async function act2Cards(params: {
  protagonistA: Character;
  protagonistB: Character;
  history: ChatMessage[];
  metricsA: CharacterMetrics;
  metricsB: CharacterMetrics;
  topicStage: TopicStage;
  scene: string;
  usedTopics: string[];
}): Promise<Act2CardsResponse> {
  return apiPost<Act2CardsResponse>('/api/act2/cards', params);
}

export async function act2Evaluate(params: {
  protagonistA: Character;
  protagonistB: Character;
  history: ChatMessage[];
  metricsA: CharacterMetrics;
  metricsB: CharacterMetrics;
  topicStage: TopicStage;
}): Promise<Act2EvaluateResponse> {
  return apiPost<Act2EvaluateResponse>('/api/act2/evaluate', params);
}

// ============================================
// 第三幕
// ============================================

export async function act3Week(params: {
  protagonistA: Character;
  protagonistB: Character;
  week: number;
  commitmentA: number;
  commitmentB: number;
  energyA: number;
  energyB: number;
  stability: number;
  activeTrialId: string;
  chosenStrategy: string;
  narrativeLog: string[];
  act2Result?: Act2Result;
}): Promise<Act3WeekResponse> {
  return apiPost<Act3WeekResponse>('/api/act3/week', params);
}

export async function act3Evaluate(params: {
  protagonistA: Character;
  protagonistB: Character;
  commitmentA: number;
  commitmentB: number;
  energyA: number;
  energyB: number;
  stability: number;
  narrativeLog: string[];
  act2Result?: Act2Result;
}): Promise<Act3EvaluateResponse> {
  return apiPost<Act3EvaluateResponse>('/api/act3/evaluate', params);
}

// ============================================
// 复盘报告
// ============================================

export async function generateReport(params: {
  protagonistA: Character;
  protagonistB: Character;
  act1Result: Act1Result;
  act2Result: Act2Result;
  act3Result: Act3Result;
  act1Highlights: string[];
  act2Highlights: string[];
  act3Highlights: string[];
}): Promise<FinalReport> {
  const res = await apiPost<GenerateReportResponse>('/api/generate-report', params);
  return res.report;
}
